import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useAuth } from '../contexts/AuthContext';
import type { ManagerCheckInPreference } from '../types';
import { checkInOptOutAcknowledgmentPoints } from '../utils/checkInOptOutCopy';
import { emailOptOutAcknowledgmentText } from '../utils/emailOptOutCopy';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotUpdate } from '../utils/userFacingCopy';

interface EmailPreferencesStatus {
  receiveAllEmails: boolean;
  acknowledgmentText?: string;
  mustReaffirmAcknowledgment?: boolean;
}

interface OptOutReaffirmGateProps {
  children: React.ReactNode;
}

const MANAGER_ROLES = new Set(['Manager', 'SeniorManager', 'Administrator', 'TeamLead']);

/**
 * Single login dialog when email and/or check-in opt-out acknowledgment wording has changed.
 * Governance remains a separate gate. Users can reaffirm each section or turn that feature back on.
 */
export default function OptOutReaffirmGate({ children }: OptOutReaffirmGateProps) {
  const { user } = useAuth();
  const mayHaveCheckInOptOut = !!user?.roles?.some((role) => MANAGER_ROLES.has(role));

  const [emailStatus, setEmailStatus] = useState<EmailPreferencesStatus | null>(null);
  const [checkInPreference, setCheckInPreference] = useState<ManagerCheckInPreference | null>(null);
  const [loading, setLoading] = useState(!!user?.id);
  const [emailAckChecked, setEmailAckChecked] = useState(false);
  const [checkInAckChecked, setCheckInAckChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchStatuses = useCallback(async () => {
    if (!user?.id) {
      setEmailStatus(null);
      setCheckInPreference(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const emailPromise = apiService
        .get<EmailPreferencesStatus>(API_ENDPOINTS.USERS.EMAIL_PREFERENCES)
        .then((res) => res.data)
        .catch(() => null);

      const checkInPromise = mayHaveCheckInOptOut
        ? apiService
            .get<ManagerCheckInPreference>(API_ENDPOINTS.CHECK_INS.MANAGER_PREFERENCE)
            .then((res) => res.data)
            .catch(() => null)
        : Promise.resolve(null);

      const [email, checkIn] = await Promise.all([emailPromise, checkInPromise]);
      setEmailStatus(email);
      setCheckInPreference(checkIn);
    } finally {
      setLoading(false);
    }
  }, [user?.id, mayHaveCheckInOptOut]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const needEmail = !!emailStatus?.mustReaffirmAcknowledgment;
  const needCheckIn = !!checkInPreference?.mustReaffirmAcknowledgment;
  const dialogOpen = needEmail || needCheckIn;

  const emailText = emailStatus?.acknowledgmentText?.trim() || emailOptOutAcknowledgmentText;
  const checkInPoints =
    checkInPreference?.acknowledgmentPoints?.length
      ? checkInPreference.acknowledgmentPoints
      : [...checkInOptOutAcknowledgmentPoints];

  const canConfirmKeep =
    (!needEmail || emailAckChecked) && (!needCheckIn || checkInAckChecked);

  const handleConfirmKeep = async () => {
    if (!canConfirmKeep || submitting) return;
    try {
      setSubmitting(true);
      setError('');
      if (needEmail) {
        await apiService.put(API_ENDPOINTS.USERS.EMAIL_PREFERENCES_REAFFIRM_OPT_OUT, {
          acknowledged: true,
        });
      }
      if (needCheckIn) {
        await apiService.put(API_ENDPOINTS.CHECK_INS.MANAGER_PREFERENCE_REAFFIRM, {
          acknowledged: true,
        });
      }
      setEmailAckChecked(false);
      setCheckInAckChecked(false);
      await fetchStatuses();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotUpdate('your preferences')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTurnEmailsBackOn = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      setError('');
      await apiService.put(API_ENDPOINTS.USERS.EMAIL_PREFERENCES, {
        receiveAllEmails: true,
        receiveReviewEmails: true,
        receiveCheckInEmails: true,
        receiveIdpEmails: true,
        receiveProfileChangeEmails: true,
        receiveExpenseClaimEmails: true,
      });
      setEmailAckChecked(false);
      await fetchStatuses();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotUpdate('your email preferences')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTurnCheckInsBackOn = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      setError('');
      await apiService.put(API_ENDPOINTS.CHECK_INS.MANAGER_PREFERENCE_OPT_IN);
      setCheckInAckChecked(false);
      await fetchStatuses();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotUpdate('your check-in preference')));
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.id && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const bothNeeded = needEmail && needCheckIn;

  return (
    <>
      {children}
      <Dialog
        open={dialogOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          {bothNeeded
            ? 'Acknowledgment updates'
            : needEmail
              ? 'Email notification acknowledgment updated'
              : 'Built-in check-ins acknowledgment updated'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {bothNeeded
              ? 'Some of your opt-out acknowledgments have changed. For each, confirm you still want to stay opted out, or turn that feature back on.'
              : needEmail
                ? 'The acknowledgment for turning off optional emails has changed. Please confirm you still want optional emails off, or turn them back on.'
                : 'The acknowledgment for turning off built-in check-ins has changed. Please confirm you still manage ongoing 1:1 conversations off-system, or turn built-in check-ins back on.'}
          </Typography>

          {needEmail && (
            <Box sx={{ mb: needCheckIn ? 3 : 0 }}>
              {bothNeeded && (
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Optional emails
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {emailText}
              </Typography>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', display: 'block', mb: 1 }}
                control={
                  <Checkbox
                    checked={emailAckChecked}
                    onChange={(e) => setEmailAckChecked(e.target.checked)}
                    disabled={submitting}
                  />
                }
                label="I have read and confirm the above — keep optional emails turned off."
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleTurnEmailsBackOn}
                disabled={submitting}
                sx={{ mt: 0.5 }}
              >
                Turn optional emails back on
              </Button>
            </Box>
          )}

          {needEmail && needCheckIn && <Divider sx={{ my: 2 }} />}

          {needCheckIn && (
            <Box>
              {bothNeeded && (
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Built-in check-ins
                </Typography>
              )}
              <List dense disablePadding>
                {checkInPoints.map((point) => (
                  <ListItem key={point} sx={{ alignItems: 'flex-start', py: 0.5, px: 0 }}>
                    <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', display: 'block', mt: 1, mb: 1 }}
                control={
                  <Checkbox
                    checked={checkInAckChecked}
                    onChange={(e) => setCheckInAckChecked(e.target.checked)}
                    disabled={submitting}
                  />
                }
                label="I have read and confirm the above — I still manage check-ins off-system."
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleTurnCheckInsBackOn}
                disabled={submitting}
                sx={{ mt: 0.5 }}
              >
                Turn built-in check-ins back on
              </Button>
            </Box>
          )}

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleConfirmKeep}
            disabled={!canConfirmKeep || submitting}
          >
            {submitting ? (
              <CircularProgress size={22} />
            ) : bothNeeded ? (
              'Confirm — keep opted out'
            ) : needEmail ? (
              'Confirm — keep emails off'
            ) : (
              'Confirm — keep off-system'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
